import { useCallback, useMemo } from "react";

import { AnalyticsService } from "@/lib/analytics/analytics.service";
import { useAuth } from "@/lib/contexts/auth.context";
import { useCoinBalance } from "@/lib/coin/coin.store";
import { useSubscription } from "@/lib/subscription/useSubscription.hook";

import { NudgeStore } from "./nudge.store";
import type { NudgeContext, NudgeId } from "./nudge.types";

const FREE_STATUSES = new Set(["NONE", "CANCELED", "INCOMPLETE_EXPIRED"]);

/**
 * Single eligibility rule for value-based trial / upgrade nudges:
 * free or cancelled users who have never used the Creator trial.
 */
export function isTrialNudgeEligible(
	subscriptionStatus: string | undefined | null,
	hasUsedSubscriptionTrial: boolean | undefined,
): boolean {
	const status = subscriptionStatus ?? "NONE";
	return FREE_STATUSES.has(status) && hasUsedSubscriptionTrial !== true;
}

/**
 * Orchestrates upgrade nudges: eligibility, pacing (1/session + 48h cooldown),
 * and uniform analytics so every surface is comparable.
 */
export function useUpgradeNudge() {
	const { user, isAuthenticated } = useAuth();
	const { subscriptionStatus } = useSubscription();
	const { balance: coinBalance } = useCoinBalance();

	const eligible = useMemo(() => {
		if (!isAuthenticated || !user?.uid) return false;
		return isTrialNudgeEligible(
			subscriptionStatus,
			user.hasUsedSubscriptionTrial,
		);
	}, [
		isAuthenticated,
		user?.uid,
		user?.hasUsedSubscriptionTrial,
		subscriptionStatus,
	]);

	const canShow = useCallback(
		(nudgeId: NudgeId): boolean => {
			if (!eligible || !user?.uid) return false;
			return NudgeStore.canShow(user.uid, nudgeId);
		},
		[eligible, user?.uid],
	);

	const baseProps = useCallback(
		(nudgeId: NudgeId, context?: NudgeContext) => ({
			nudge_id: nudgeId,
			coin_balance: context?.coin_balance ?? coinBalance ?? undefined,
			podcast_id: context?.podcast_id,
			podcast_count: context?.podcast_count,
			minutes_listened: context?.minutes_listened,
			trigger: context?.trigger,
			missing_coins: context?.missing_coins,
			required_coins: context?.required_coins,
		}),
		[coinBalance],
	);

	const markShown = useCallback(
		(nudgeId: NudgeId, context?: NudgeContext) => {
			if (!user?.uid) return;
			NudgeStore.markShown(user.uid, nudgeId);
			AnalyticsService.track(
				"upgrade_nudge_shown",
				baseProps(nudgeId, context),
			);
		},
		[user?.uid, baseProps],
	);

	const markClicked = useCallback(
		(nudgeId: NudgeId, context?: NudgeContext) => {
			if (!user?.uid) return;
			NudgeStore.markShown(user.uid, nudgeId);
			AnalyticsService.track(
				"upgrade_nudge_clicked",
				baseProps(nudgeId, context),
			);
		},
		[user?.uid, baseProps],
	);

	const markDismissed = useCallback(
		(nudgeId: NudgeId, context?: NudgeContext) => {
			if (!user?.uid) return;
			NudgeStore.markDismissed(user.uid, nudgeId);
			AnalyticsService.track(
				"upgrade_nudge_dismissed",
				baseProps(nudgeId, context),
			);
		},
		[user?.uid, baseProps],
	);

	const incrementCreateFlow = useCallback((): number => {
		if (!user?.uid) return 0;
		return NudgeStore.incrementCreateFlowCount(user.uid);
	}, [user?.uid]);

	const createFlowCount = user?.uid
		? NudgeStore.getCreateFlowCount(user.uid)
		: 0;

	return {
		eligible,
		canShow,
		markShown,
		markClicked,
		markDismissed,
		incrementCreateFlow,
		createFlowCount,
		coinBalance,
		userId: user?.uid,
	};
}
